import React, { useEffect, useState } from "react"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Textarea } from "@/components/ui/textarea"
import { StageMoveDialogConfig } from "@/config/stageMoveDialogs"

interface StageMoveDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  config: StageMoveDialogConfig
  applicantName: string
  onConfirm: (data: { stage: string; reason?: string; note?: string; salary?: number }) => void
  isUpdating?: boolean
  initialSalary?: number | null
}

const StageMoveDialog: React.FC<StageMoveDialogProps> = ({
  open,
  onOpenChange,
  config,
  applicantName,
  onConfirm,
  isUpdating = false,
  initialSalary = null,
}) => {
  // For now we support only the "reason_note" type
  const [reason, setReason] = useState(
    config.reasons && config.reasons.length > 0 ? config.reasons[0].value : "",
  )
  const [note, setNote] = useState("")
  const [salaryValue, setSalaryValue] = useState<string>(
    initialSalary != null
      ? String(initialSalary)
      : config.defaultSalary != null
        ? String(config.defaultSalary)
        : "",
  )

  useEffect(() => {
    // Reset defaults when config changes
    setReason(config.reasons && config.reasons.length > 0 ? config.reasons[0].value : "")
    setNote("")
    setSalaryValue(
      initialSalary != null
        ? String(initialSalary)
        : config.defaultSalary != null
          ? String(config.defaultSalary)
          : "",
    )
  }, [config, initialSalary])

  const isSalaryDialog = config.type === "salary_input"
  const parsedSalary = isSalaryDialog ? Number(salaryValue !== "" ? salaryValue : NaN) : undefined
  const salaryInvalid = isSalaryDialog
    ? Number.isNaN(parsedSalary) || (parsedSalary as number) <= 0
    : false

  const handleConfirm = () => {
    if (isSalaryDialog) {
      if (salaryInvalid) return
      onConfirm({ stage: config.stage, salary: parsedSalary })
    } else {
      onConfirm({ stage: config.stage, reason, note })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{config.title}</DialogTitle>
          <DialogDescription>
            {config.description.replace("{name}", applicantName)}
          </DialogDescription>
        </DialogHeader>

        {config.type === "reason_note" && (
          <div className="py-4">
            {config.reasons && config.reasons.length > 0 && (
              <RadioGroup value={reason} onValueChange={setReason}>
                {config.reasons.map((reasonOption) => (
                  <div key={reasonOption.value} className="flex items-center space-x-2 mb-2">
                    <RadioGroupItem
                      value={reasonOption.value}
                      id={`${config.stage}-${reasonOption.value}`}
                    />
                    <Label htmlFor={`${config.stage}-${reasonOption.value}`}>
                      {reasonOption.label}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            )}

            <div className="mt-4">
              <Label htmlFor={`${config.stage}-note`}>Additional Notes (Optional)</Label>
              <Textarea
                id={`${config.stage}-note`}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Add any additional information here..."
                className="mt-1"
              />
            </div>
          </div>
        )}

        {config.type === "salary_input" && (
          <div className="py-4 space-y-3">
            <div>
              <Label htmlFor={`${config.stage}-salary`}>Contract Salary</Label>
              <input
                id={`${config.stage}-salary`}
                type="number"
                className="mt-1 w-full rounded-md border px-3 py-2 text-sm"
                value={salaryValue}
                onChange={(e) => setSalaryValue(e.target.value)}
                placeholder="Enter salary"
                min={0}
                step={1}
              />
              {salaryInvalid && (
                <p className="text-xs text-red-600 mt-1">
                  Please enter a valid salary greater than 0.
                </p>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isUpdating}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={isUpdating || salaryInvalid}>
            {isUpdating ? "Updating..." : config.confirmButtonText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default StageMoveDialog
